import { motion } from "framer-motion";
import { Calendar, MessageCircle, Phone, Play, Video } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface VirtualTourProps {
  tourUrl?: string;
  title: string;
  previewImage: string;
}

const WHATSAPP_NUMBER = "37060044499";

export const VirtualTour = ({ tourUrl, title, previewImage }: VirtualTourProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleRequestTour = () => {
    const contactForm = document.querySelector('[data-contact-form]');
    if (contactForm) {
      contactForm.scrollIntoView({ behavior: "smooth" });
    }
  };

  const openWhatsApp = () => {
    const message = `Hello, I'm interested in scheduling a private tour of ${title}. Please contact me with available times.`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, "_blank");
  };

  const safeTourUrl = useMemo(() => {
    if (!tourUrl) return undefined;
    try {
      const url = new URL(tourUrl);
      return url.toString();
    } catch {
      return undefined;
    }
  }, [tourUrl]);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="relative aspect-video overflow-hidden rounded-lg bg-charcoal"
      >
        <img
          src={previewImage}
          alt={`${title} virtual tour preview`}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/60 to-transparent" />

        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 sm:px-6 text-center">
          {/* Icon */}
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full border-2 border-gold bg-gold/20 md:mb-6 md:h-18 md:w-18">
            <Video className="text-gold" size={24} />
          </div>

          {/* Title */}
          <h3 className="mb-2 font-display text-xl text-cream md:mb-3 md:text-2xl lg:text-3xl">
            Private Virtual Tour
          </h3>
          <p className="mb-6 max-w-md font-body text-sm text-cream/70 md:mb-8 md:text-base">
            Experience an exclusive walkthrough of this exceptional property
          </p>

          {/* Action Buttons - 2x2 grid on mobile, row on desktop */}
          <div className="grid grid-cols-2 gap-3 w-full max-w-md px-2 sm:flex sm:flex-wrap sm:justify-center sm:gap-3">
            {/* Primary CTA */}
            {safeTourUrl ? (
              <Button
                onClick={() => setIsOpen(true)}
                variant="luxury"
                size="lg"
                className="w-full sm:w-auto sm:min-w-[140px]"
              >
                <Play size={18} className="shrink-0" />
                <span>Watch Tour</span>
              </Button>
            ) : (
              <Button
                onClick={handleRequestTour}
                variant="luxury"
                size="lg"
                className="w-full sm:w-auto sm:min-w-[140px]"
              >
                <Calendar size={18} className="shrink-0" />
                <span>Request Tour</span>
              </Button>
            )}

            {/* Schedule */}
            <Button
              onClick={handleRequestTour}
              variant="outline-light"
              size="lg"
              className="w-full sm:w-auto sm:min-w-[120px]"
            >
              <Calendar size={18} className="shrink-0" />
              <span>Schedule</span>
            </Button>

            {/* WhatsApp */}
            <Button
              onClick={openWhatsApp}
              variant="whatsapp"
              size="lg"
              className="w-full sm:w-auto sm:min-w-[120px]"
            >
              <MessageCircle size={18} className="shrink-0" />
              <span>WhatsApp</span>
            </Button>

            {/* Call */}
            <Button
              variant="outline-light"
              size="lg"
              className="w-full sm:w-auto sm:min-w-[120px]"
              asChild
            >
              <a href="tel:+37060044499">
                <Phone size={18} className="shrink-0" />
                <span>Call Now</span>
              </a>
            </Button>
          </div>

          {/* Footer text */}
          <p className="mt-4 font-body text-xs text-cream/50 md:mt-6 md:text-sm">
            Tours available 7 days a week • In-person & virtual options
          </p>
        </div>
      </motion.div>

      {safeTourUrl ? (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="max-w-5xl">
            <DialogHeader>
              <DialogTitle className="font-display">{title} – Virtual Tour</DialogTitle>
            </DialogHeader>
            <div className="aspect-video w-full overflow-hidden rounded-md bg-card">
              <iframe
                title={`${title} virtual tour`}
                src={safeTourUrl}
                className="h-full w-full"
                loading="lazy"
                referrerPolicy="no-referrer"
                allow="fullscreen; autoplay; xr-spatial-tracking; gyroscope; accelerometer"
              />
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
};
